/**
 * AviPLANPanel
 * ============
 * Floating control panel (child of UnifiedAirportMap) for computing and
 * displaying AviPLAN geometry layers:
 *   - Swept path (aircraft turning arc envelope)
 *   - Safety envelope (ICAO Annex 14 clearance)
 *   - Jet blast zone
 *
 * Uses useMapEngine() to access the Mapbox map instance for click-to-place.
 * Calls the /aircraft-geometry backend API.
 * Reports results via onUpdate(data) — parent stores and passes back as
 * aviplanData prop on UnifiedAirportMap.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMapEngine } from './MapEngineContext';
import { aircraftGeometryService } from '../../services/aircraftGeometryService';
import type { FeatureCollection } from 'geojson';
import mapboxgl from 'mapbox-gl';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AviPLANData {
  sweptPaths?: FeatureCollection | null;
  safetyEnvelopes?: FeatureCollection | null;
  jetBlast?: FeatureCollection | null;
}

interface Props {
  onUpdate: (data: AviPLANData) => void;
}

type Tab = 'swept' | 'envelope' | 'blast';

const TABS: { id: Tab; label: string }[] = [
  { id: 'swept',    label: 'Swept Path'  },
  { id: 'envelope', label: 'Envelope'    },
  { id: 'blast',    label: 'Jet Blast'   },
];

const PHASES = ['idle', 'breakaway', 'taxi', 'takeoff'] as const;

// ─── Position picker marker ───────────────────────────────────────────────────

function useMapPicker(map: mapboxgl.Map | null, mapLoaded: boolean) {
  const [picking, setPicking] = useState(false);
  const [pos, setPos]         = useState<[number, number] | null>(null);
  const markerRef             = useRef<mapboxgl.Marker | null>(null);

  const startPick = useCallback(() => setPicking(true), []);

  useEffect(() => {
    if (!map || !mapLoaded || !picking) return;

    const cursor = map.getCanvas().style.cursor;
    map.getCanvas().style.cursor = 'crosshair';

    const handler = (e: mapboxgl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      setPos([lng, lat]);
      setPicking(false);
      map.getCanvas().style.cursor = cursor;

      // Update or create pin marker
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        const el = document.createElement('div');
        el.style.cssText = 'width:12px;height:12px;border-radius:50%;background:#F26430;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);';
        markerRef.current = new mapboxgl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(map);
      }
    };

    map.once('click', handler);
    return () => {
      map.off('click', handler);
      map.getCanvas().style.cursor = cursor;
    };
  }, [map, mapLoaded, picking]);

  // Clean up marker on unmount
  useEffect(() => () => { markerRef.current?.remove(); }, []);

  return { picking, pos, startPick, setPos };
}

// ─── AviPLANPanel ─────────────────────────────────────────────────────────────

const AviPLANPanel: React.FC<Props> = ({ onUpdate }) => {
  const { map, mapLoaded } = useMapEngine();
  const [open, setOpen]   = useState(false);
  const [tab,  setTab]    = useState<Tab>('swept');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  // Shared position picker
  const picker = useMapPicker(map, mapLoaded);

  // ── Swept path form state ──────────────────────────────────────────────────
  const [spType,    setSpType]    = useState('B738');
  const [spHdg,     setSpHdg]    = useState(180);
  const [spAngle,   setSpAngle]  = useState(90);
  const [spDir,     setSpDir]    = useState<'left' | 'right'>('right');

  // ── Safety envelope form state ─────────────────────────────────────────────
  const [seType,    setSeType]   = useState('B738');
  const [seHdg,     setSeHdg]    = useState(180);
  const [seMode,    setSeMode]   = useState<'icao_minimum' | 'operational'>('icao_minimum');

  // ── Jet blast form state ───────────────────────────────────────────────────
  const [jbType,    setJbType]   = useState('B738');
  const [jbHdg,     setJbHdg]    = useState(180);
  const [jbPhase,   setJbPhase]  = useState<typeof PHASES[number]>('breakaway');
  const [jbMulti,   setJbMulti]  = useState(false);

  // ─── Compute handlers ──────────────────────────────────────────────────────

  const compute = async () => {
    if (!picker.pos) { setError('Click "Set position" then click on the map.'); return; }
    setError('');
    setBusy(true);
    const [lon, lat] = picker.pos;

    try {
      if (tab === 'swept') {
        const feat = await aircraftGeometryService.getSweptPath({
          icao_type: spType.toUpperCase(),
          start_lon: lon, start_lat: lat,
          start_heading_deg: spHdg,
          total_angle_deg: spAngle,
          turn_direction: spDir,
        });
        onUpdate({ sweptPaths: { type: 'FeatureCollection', features: [feat as any] } });
      } else if (tab === 'envelope') {
        const feat = await aircraftGeometryService.getSafetyEnvelope({
          icao_type: seType.toUpperCase(),
          center_lon: lon, center_lat: lat,
          heading_deg: seHdg, mode: seMode,
        });
        onUpdate({ safetyEnvelopes: { type: 'FeatureCollection', features: [feat as any] } });
      } else {
        const result = await aircraftGeometryService.getJetBlast({
          icao_type: jbType.toUpperCase(),
          engine_lon: lon, engine_lat: lat,
          aircraft_heading_deg: jbHdg,
          phase: jbPhase,
          multi_engine: jbMulti,
        });
        const features = result.type === 'FeatureCollection'
          ? (result as GeoJSON.FeatureCollection).features
          : [result as GeoJSON.Feature];
        onUpdate({ jetBlast: { type: 'FeatureCollection', features: features as any[] } });
      }
    } catch (err) {
      setError('API error — check backend is running.');
      console.error('[AviPLANPanel]', err);
    } finally {
      setBusy(false);
    }
  };

  const clearAll = () => {
    onUpdate({ sweptPaths: null, safetyEnvelopes: null, jetBlast: null });
    picker.setPos(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="absolute bottom-10 left-3 z-20">
      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        title="AviPLAN geometry tools"
        className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold shadow-lg transition-all ${
          open
            ? 'bg-[#F26430] text-white'
            : 'bg-[#13131a]/90 text-[#F26430] border border-[#F26430]/40 hover:border-[#F26430] backdrop-blur-sm'
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
        AviPLAN
      </button>

      {/* Panel */}
      {open && (
        <div className="mt-2 w-64 rounded-xl border border-[#2a2a30] bg-[#13131a]/97 backdrop-blur-sm shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2a2a30]">
            <span className="text-xs font-semibold text-white">AviPLAN Geometry</span>
            <button onClick={clearAll} className="text-[10px] text-[var(--text-tertiary)] hover:text-red-400 transition-colors">
              Clear all
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#2a2a30]">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 py-2 text-[10px] font-medium transition-colors ${
                  tab === t.id
                    ? 'bg-[#F26430]/15 text-[#F26430] border-b-2 border-[#F26430]'
                    : 'text-[var(--text-secondary)] hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Form */}
          <div className="p-4 space-y-3">
            {/* Position picker (shared) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] text-[var(--text-secondary)]">Position</label>
                <button
                  onClick={picker.startPick}
                  disabled={picker.picking}
                  className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
                    picker.picking
                      ? 'bg-[#F26430]/20 text-[#F26430] animate-pulse'
                      : 'bg-[var(--surface-raised)] text-[var(--text-secondary)] hover:text-white'
                  }`}
                >
                  {picker.picking ? 'Click map…' : 'Set position'}
                </button>
              </div>
              {picker.pos ? (
                <div className="font-mono text-[10px] text-[var(--text-tertiary)] bg-[#1e1e24] rounded px-2 py-1">
                  {picker.pos[1].toFixed(5)}, {picker.pos[0].toFixed(5)}
                </div>
              ) : (
                <div className="text-[10px] text-[var(--text-tertiary)] italic">Not set</div>
              )}
            </div>

            {/* Tab-specific fields */}
            {tab === 'swept' && (
              <>
                <Field label="Aircraft type">
                  <input value={spType} onChange={(e) => setSpType(e.target.value)}
                    className={INPUT_CLS} placeholder="B738" />
                </Field>
                <Field label="Start heading (°)">
                  <input type="number" value={spHdg} min={0} max={359}
                    onChange={(e) => setSpHdg(Number(e.target.value))} className={INPUT_CLS} />
                </Field>
                <Field label="Turn angle (°)">
                  <input type="number" value={spAngle} min={1} max={360}
                    onChange={(e) => setSpAngle(Number(e.target.value))} className={INPUT_CLS} />
                </Field>
                <Field label="Direction">
                  <div className="flex gap-1">
                    {(['left', 'right'] as const).map((d) => (
                      <button key={d} onClick={() => setSpDir(d)}
                        className={`flex-1 py-1 rounded text-[10px] font-medium capitalize transition-colors ${
                          spDir === d ? 'bg-[#F26430] text-white' : 'bg-[var(--surface-raised)] text-[var(--text-secondary)]'
                        }`}
                      >{d}</button>
                    ))}
                  </div>
                </Field>
              </>
            )}

            {tab === 'envelope' && (
              <>
                <Field label="Aircraft type">
                  <input value={seType} onChange={(e) => setSeType(e.target.value)}
                    className={INPUT_CLS} placeholder="B738" />
                </Field>
                <Field label="Heading (°)">
                  <input type="number" value={seHdg} min={0} max={359}
                    onChange={(e) => setSeHdg(Number(e.target.value))} className={INPUT_CLS} />
                </Field>
                <Field label="Mode">
                  <div className="flex gap-1">
                    {(['icao_minimum', 'operational'] as const).map((m) => (
                      <button key={m} onClick={() => setSeMode(m)}
                        className={`flex-1 py-1 rounded text-[10px] font-medium transition-colors ${
                          seMode === m ? 'bg-[#F26430] text-white' : 'bg-[var(--surface-raised)] text-[var(--text-secondary)]'
                        }`}
                      >{m === 'icao_minimum' ? 'ICAO min.' : 'Operational'}</button>
                    ))}
                  </div>
                </Field>
              </>
            )}

            {tab === 'blast' && (
              <>
                <Field label="Aircraft type">
                  <input value={jbType} onChange={(e) => setJbType(e.target.value)}
                    className={INPUT_CLS} placeholder="B738" />
                </Field>
                <Field label="Heading (°)">
                  <input type="number" value={jbHdg} min={0} max={359}
                    onChange={(e) => setJbHdg(Number(e.target.value))} className={INPUT_CLS} />
                </Field>
                <Field label="Phase">
                  <select value={jbPhase} onChange={(e) => setJbPhase(e.target.value as any)}
                    className={INPUT_CLS}>
                    {PHASES.map((p) => (
                      <option key={p} value={p} className="bg-[#1e1e24] capitalize">{p}</option>
                    ))}
                  </select>
                </Field>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="jbMulti" checked={jbMulti}
                    onChange={(e) => setJbMulti(e.target.checked)}
                    className="accent-[#F26430]" />
                  <label htmlFor="jbMulti" className="text-[10px] text-[var(--text-secondary)] cursor-pointer">
                    Per-engine zones
                  </label>
                </div>
              </>
            )}

            {/* Error */}
            {error && (
              <div className="text-[10px] text-red-400 bg-red-400/10 rounded px-2 py-1.5">{error}</div>
            )}

            {/* Compute button */}
            <button
              onClick={compute}
              disabled={busy || !picker.pos}
              className={`w-full rounded-lg py-2 text-xs font-semibold transition-all ${
                busy || !picker.pos
                  ? 'bg-[var(--surface-raised)] text-[var(--text-tertiary)] cursor-not-allowed'
                  : 'bg-[#F26430] text-white hover:bg-[#d14f1f]'
              }`}
            >
              {busy ? 'Computing…' : 'Compute'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Small helper components ──────────────────────────────────────────────────

const INPUT_CLS =
  'w-full rounded-lg border border-[#2a2a30] bg-[#1e1e24] px-2 py-1.5 text-[10px] text-white focus:outline-none focus:border-[#F26430]';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[10px] text-[var(--text-secondary)] mb-1">{label}</label>
    {children}
  </div>
);

export default AviPLANPanel;
